import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { comparePriority } from "@restomenu/core/contract";
import { useAppSelector } from "@/core/hooks";
import { useMenuData } from "@/core/menu/useMenuData";
import {
  applyFilters,
  clearFilters,
  clearSearch as clearSearchAction,
  setSearchQuery,
} from "@/core/menu/reducer";
import {
  selectActiveFilters,
  selectHasActiveFilters,
  selectSearchQuery,
} from "@/core/menu/selectors";
import type { ActiveFilters } from "@/core/menu/utils";
import {
  applyTagFilters,
  filterMainProducts,
  getCategoriesByProducts,
  groupProductsByCategories,
  searchProducts,
  sortProducts,
} from "@/core/menu/utils";

/**
 * Фасад меню.
 *
 * Ф4б: набор данных сохранён, сигнатура — нет. Прежние get*-хелперы звали
 * useSelector ВНУТРИ возвращаемого колбэка, то есть число вызовов хуков
 * зависело от того, сколько раз потребитель дёрнул хелпер за рендер, —
 * нарушение rules of hooks, на которое eslint и ругался семью ошибками.
 * Теперь это чистые функции над уже полученными данными.
 */
export const useMenu = () => {
  const dispatch = useDispatch();
  const pointId = useAppSelector((state) => state.app.pointId);

  const {
    categories: allCategories,
    products: allProducts,
    tags,
    variantProperties,
    isVariantPropertiesLoading,
    isLoading,
    error,
    isPartial,
  } = useMenuData(pointId);

  const searchQuery = useSelector(selectSearchQuery);
  const activeFilters = useSelector(selectActiveFilters);
  const hasActiveFilters = useSelector(selectHasActiveFilters);

  const mainProducts = useMemo(
    () => filterMainProducts(allProducts),
    [allProducts],
  );

  const filteredProducts = useMemo(
    () =>
      sortProducts(
        applyTagFilters(mainProducts, activeFilters),
        activeFilters.sortByPrice,
      ),
    [mainProducts, activeFilters],
  );

  // Навигация строится по категориям, в которых есть отфильтрованные
  // товары: при активном фильтре список категорий сужается вместе с ним.
  const categories = useMemo(
    () => getCategoriesByProducts(filteredProducts, allCategories),
    [filteredProducts, allCategories],
  );

  const groupedProducts = useMemo(
    () => groupProductsByCategories(filteredProducts, categories),
    [filteredProducts, categories],
  );

  const searchResults = useMemo(
    () => filterMainProducts(searchProducts(allProducts, searchQuery)),
    [allProducts, searchQuery],
  );

  const activeProducts = useMemo(
    () => allProducts.filter((product) => product.isEnabled),
    [allProducts],
  );

  const categoriesSortedByPriority = useMemo(
    () =>
      [...categories].sort((a, b) => comparePriority(a.priority, b.priority)),
    [categories],
  );

  const categoriesWithProductCount = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        productCount: groupedProducts[category.id]?.products.length ?? 0,
      })),
    [categories, groupedProducts],
  );

  const displayProducts = searchQuery.trim() ? searchResults : filteredProducts;

  return {
    // State
    categories,
    products: mainProducts,
    filteredProducts,
    tags,
    variantProperties,
    variantPropertiesLoading: isVariantPropertiesLoading,
    groupedProducts,
    searchQuery,
    searchResults,
    loading: isLoading,
    error,
    isPartial,

    // Filters
    activeFilters,
    hasActiveFilters,

    // Derived
    searchResultsMemoized: searchResults,
    filteredProductsBySearch: displayProducts,
    displayProducts,
    activeProducts,
    productsCount: allProducts.length,
    filteredProductsCount: filteredProducts.length,
    categoriesCount: categories.length,
    tagsCount: tags.length,
    searchResultsCount: searchResults.length,
    categoriesSortedByPriority,
    categoriesWithProductCount,

    // Actions
    searchProductsByQuery: (query: string) => dispatch(setSearchQuery(query)),
    clearSearch: () => dispatch(clearSearchAction()),
    applyProductFilters: (filters: ActiveFilters) =>
      dispatch(applyFilters(filters)),
    clearProductFiltersAction: () => dispatch(clearFilters()),

    // Селекторы над результатом — были хелперами с useSelector в колбэке
    //
    // Ищем и внутри групп: варианты верхним уровнем в /guest/products не
    // выходят, а ссылка на модалку товара может нести id варианта.
    getProductById: (productId: string) => {
      for (const product of allProducts) {
        if (product.id === productId) return product;
        const variant = product.variants?.find((v) => v.id === productId);
        if (variant) return variant;
      }
      return null;
    },
    getCategoryById: (categoryId: string) =>
      categories.find((category) => category.id === categoryId) ?? null,
    getTagById: (tagId: string) => tags.find((tag) => tag.id === tagId) ?? null,
    getProductsByCategory: (categoryId: string) =>
      groupedProducts[categoryId]?.products ?? [],
    productExists: (productId: string) =>
      allProducts.some((product) => product.id === productId),
    categoryExists: (categoryId: string) =>
      categories.some((category) => category.id === categoryId),
    tagExists: (tagId: string) => tags.some((tag) => tag.id === tagId),
  };
};
